/**
 * DNS record card for the policy page — advise + verify: we SHOW the record
 * name + TXT value the customer must publish, and (when allowed) an Advance
 * button. The portal never edits the customer's DNS itself.
 */
import { Loader2 } from "lucide-react";
import { Button, CopyButton } from "@ddmarc/ui";
import type { PortalRollout } from "@/lib/portal-api";
import { CardV2 } from "@/components/primitives";

export function DnsRecordCard({
  rollout,
  onAdvance,
  isAdvancing,
}: {
  rollout: PortalRollout;
  onAdvance: () => void;
  isAdvancing: boolean;
}) {
  return (
    <CardV2
      title="Publish this DNS record"
      subtitle="Then re-check DNS on the Domains page to verify it's live."
    >
      <div className="space-y-3">
        {/* Advise + verify: we show the record, the customer publishes it. */}
        <div className="space-y-1">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
            Record name
          </span>
          <div className="flex items-center gap-2">
            <code className="font-mono-data flex-1 truncate rounded bg-secondary px-2 py-1.5 text-[12px] text-foreground">
              {rollout.dns_record_name}
            </code>
            <CopyButton text={rollout.dns_record_name} />
          </div>
        </div>
        <div className="space-y-1">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
            TXT value
          </span>
          <div className="flex items-center gap-2">
            <code className="font-mono-data flex-1 break-all rounded bg-secondary px-2 py-1.5 text-[12px] text-foreground">
              {rollout.current_dns_record}
            </code>
            <CopyButton text={rollout.current_dns_record} />
          </div>
        </div>
        <p className="text-[11.5px] text-muted-foreground">
          Publish this TXT record at your DNS provider, then re-check DNS on the Domains
          page. Once it's live and the safety checks pass, you can advance to the next stage.
        </p>
        {rollout.can_advance && (
          <div className="space-y-1.5">
            <Button
              onClick={onAdvance}
              disabled={isAdvancing || rollout.advance_gate_passed === false}
              className="gap-1.5"
            >
              {isAdvancing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Advance to next stage
            </Button>
            {/* Gate preview: the server would 409 this advance — say why up
                front instead of letting the click fail. */}
            {rollout.advance_gate_passed === false && rollout.advance_gate_reason && (
              <p className="text-[11.5px] text-[var(--v2-warn)]">
                Not ready to advance yet: {rollout.advance_gate_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </CardV2>
  );
}
